// CROSSSELL-109/111 / STORE-V2-CONVERSION-004 / CUSTOMER-R4.7-A / R7.2.2F2 — compact cart cross-sell.
import { useEffect, useRef, useState, type MouseEvent } from "react";
import type { CrossSellCandidate } from "./cross-sell-rules.js";
import type { CatalogV2Product } from "./catalog-v2-types.js";
import { formatUah } from "./money-v2.js";
import { resolveCustomerVisiblePrice } from "./resolve-customer-visible-price-v2.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { t } from "./store-v2-ui-copy.js";
import { miniImageUrl } from "./product-media-v2.js";
import { StoreV2Icon } from "./StoreV2Icon.js";
import type { RecommendationPlacement } from "./product-recommendations-v2.js";
import {
  dedupeActionableCandidates,
  filterRenderableActionable,
} from "./customer-actionable-product-v2.js";
import {
  resolveCustomerSellability,
  sellabilityBlocksAdd,
} from "./customer-sellability-v2.js";

export interface CrossSellBlockProps {
  lang: StoreV2Lang;
  items: CrossSellCandidate[];
  /** R7.2.2F2 — pass full product (never bare unresolved id). */
  onAdd: (product: CatalogV2Product) => void;
  onOpen?: (product: CatalogV2Product) => void;
  /** Opens cart without adding (PDP / list parity). */
  onOpenCart?: () => void;
  qtyOf?: (productId: string) => number;
  variant?: "cart" | "pdp";
  /** Override section title (PDP merchandising placements). */
  title?: string;
  testId?: string;
  onRefresh?: () => void;
  canRefresh?: boolean;
  refreshing?: boolean;
  canonical?: boolean;
  placement?: RecommendationPlacement;
  onImpression?: (ids: string[]) => void;
  /** Register shells into session map before paint (Cart known-good pattern). */
  onRegisterActionable?: (products: CatalogV2Product[]) => void;
  /** Exclude current PDP product from list. */
  excludeProductId?: string | null;
}

function XsellThumb({
  imageUrl,
  alt,
  compact,
}: {
  imageUrl: string | null | undefined;
  alt: string;
  compact: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const src = imageUrl ? String(imageUrl) : null;
  if (!src || failed) return <div className="sv2-xsell-thumb sv2-xsell-noimg">🍜</div>;
  return (
    <img
      className="sv2-xsell-thumb"
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      width={compact ? 48 : 56}
      height={compact ? 48 : 56}
      onError={() => setFailed(true)}
    />
  );
}

export function CrossSellBlock({
  lang,
  items,
  onAdd,
  onOpen,
  onOpenCart,
  qtyOf,
  variant = "cart",
  title: titleOverride,
  testId,
  onRefresh,
  canRefresh = false,
  refreshing = false,
  canonical = false,
  placement,
  onImpression,
  onRegisterActionable,
  excludeProductId,
}: CrossSellBlockProps) {
  const dedupe = useRef<Set<string>>(new Set());
  const impressed = useRef<string>("");
  const title =
    titleOverride ?? (variant === "pdp" ? t(lang, "pairs_well") : t(lang, "crosssell_title"));
  const actionableProducts = filterRenderableActionable(
    dedupeActionableCandidates(
      items.map((i) => i.product),
      excludeProductId != null && excludeProductId !== ""
        ? { excludeProductId }
        : undefined,
    ),
  );
  const actionableIdSet = new Set(actionableProducts.map((p) => p.id));
  const filteredItems = items.filter((i) => actionableIdSet.has(i.product.id));
  const list = filteredItems.slice(0, variant === "cart" ? 3 : filteredItems.length);
  const compact = variant === "cart";

  useEffect(() => {
    if (!onRegisterActionable || list.length === 0) return;
    onRegisterActionable(list.map((i) => i.product));
  }, [list, onRegisterActionable]);

  useEffect(() => {
    if (!onImpression || list.length === 0) return;
    const key = list.map((i) => i.product.id).join("|");
    if (impressed.current === key) return;
    impressed.current = key;
    onImpression(list.map((i) => i.product.id));
  }, [list, onImpression]);

  if (list.length === 0) return null;

  const addOnce = (product: CatalogV2Product, e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dedupe.current.has(product.id)) return;
    dedupe.current.add(product.id);
    onAdd(product);
    window.setTimeout(() => dedupe.current.delete(product.id), 400);
  };

  return (
    <div
      className={`sv2-xsell${compact ? " is-compact" : ""}`}
      data-testid={testId ?? "sv2-crosssell"}
      data-layout="vertical"
      data-compact={compact ? "1" : "0"}
      data-actionable-contract="r722f2"
      {...(canonical ? { "data-canonical-recs": "1" } : {})}
      {...(placement ? { "data-placement": placement } : {})}
    >
      <div className="sv2-xsell-head">
        <div className="sv2-section-title">{title}</div>
        {onRefresh ? (
          <button
            type="button"
            className={`sv2-xsell-refresh${refreshing ? " is-busy" : ""}`}
            data-testid="sv2-crosssell-refresh"
            aria-label={t(lang, "recs_show_others")}
            disabled={!canRefresh || refreshing}
            onClick={onRefresh}
          >
            <StoreV2Icon name="refresh" size={18} />
          </button>
        ) : null}
      </div>
      {variant === "cart" && !compact ? (
        <div className="sv2-muted" style={{ marginBottom: 8 }}>
          {t(lang, "crosssell_sub")}
        </div>
      ) : null}
      <div className="sv2-xsell-list" data-testid="sv2-crosssell-list">
        {list.map((item) => {
          const p = item.product;
          const price = resolveCustomerVisiblePrice(p);
          const qty = qtyOf?.(p.id) ?? 0;
          const sellability = resolveCustomerSellability(p);
          const outOfStock = sellability === "OUT_OF_STOCK" || sellabilityBlocksAdd(sellability);
          const showGoCart = variant === "pdp" && qty > 0 && onOpenCart && !outOfStock;
          return (
            <div key={p.id} className="sv2-xsell-card" data-testid={`sv2-xsell-card-${p.id}`}>
              <button
                type="button"
                className="sv2-cart-thumb-btn"
                onClick={() => onOpen?.(p)}
                disabled={!onOpen}
              >
                <XsellThumb imageUrl={miniImageUrl(p.imageUrl)} alt={item.title} compact={compact} />
              </button>
              <div className="sv2-xsell-mid">
                {onOpen ? (
                  <button type="button" className="sv2-xsell-title-btn" onClick={() => onOpen(p)}>
                    {item.title}
                  </button>
                ) : (
                  <div className="sv2-xsell-title">{item.title}</div>
                )}
                <div className="sv2-xsell-price">
                  {price.saleActive ? (
                    <>
                      <span className="sv2-price-sale">{formatUah(price.primary)}</span>{" "}
                      <span className="sv2-price-was">{formatUah(price.regular ?? 0)}</span>
                    </>
                  ) : (
                    formatUah(price.primary)
                  )}
                </div>
                {outOfStock ? (
                  <div className="sv2-xsell-oos-label">{t(lang, "out_of_stock")}</div>
                ) : null}
              </div>
              {outOfStock ? (
                <button
                  type="button"
                  className="sv2-xsell-add is-oos"
                  data-testid="sv2-crosssell-oos"
                  disabled
                >
                  {t(lang, "out_of_stock")}
                </button>
              ) : showGoCart ? (
                <button
                  type="button"
                  className="sv2-xsell-add"
                  data-testid="sv2-crosssell-go-cart"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenCart?.();
                  }}
                >
                  {t(lang, "to_cart")}
                </button>
              ) : (
                <button
                  type="button"
                  className="sv2-xsell-add"
                  data-testid="sv2-crosssell-add"
                  onClick={(e) => addOnce(p, e)}
                >
                  {t(lang, "crosssell_add")}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
